/**
 * CabinetScene — the full R3F scene with camera, lights, grid, and cabinet models.
 * Ported from viewer_3d.py scene setup (lines 5502-5625).
 */

import { useEffect, useRef, useCallback } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Grid } from '@react-three/drei';
import * as THREE from 'three';
import type { CabinetData } from './types';
import { detectAdjacency } from './dataAdapter';
import CabinetModel from './CabinetModel';

// ── Inner scene content (must be inside Canvas) ──────────────────────────────
interface SceneContentProps {
  cabinets: CabinetData[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  showHardware: boolean;
  showDrillHoles: boolean;
}

function SceneContent({ cabinets, selectedId, onSelect, showHardware, showDrillHoles }: SceneContentProps) {
  const { camera } = useThree();
  const controlsRef = useRef<any>(null);

  // Fit camera to scene bounds on first load / when cabinets change
  useEffect(() => {
    if (cabinets.length === 0) return;

    const box = new THREE.Box3();
    for (const cab of cabinets) {
      const min = new THREE.Vector3(cab.position_x, 0, cab.position_z);
      const max = new THREE.Vector3(
        cab.position_x + cab.width,
        cab.height,
        cab.position_z + cab.depth,
      );
      box.expandByPoint(min);
      box.expandByPoint(max);
    }

    const center = new THREE.Vector3();
    box.getCenter(center);
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    const distance = maxDim * 1.8;

    camera.position.set(
      center.x + distance * 0.6,
      center.y + distance * 0.5,
      center.z + distance * 0.8,
    );

    if (controlsRef.current) {
      controlsRef.current.target.copy(center);
      controlsRef.current.update();
    }
  }, [cabinets, camera]);

  const handleBackgroundClick = useCallback(() => {
    onSelect(null);
  }, [onSelect]);

  return (
    <>
      {/* Lighting — matches Python viewer: ambient 0.6 + directional 0.8 + fill 0.3 */}
      <ambientLight intensity={0.6} />
      <directionalLight
        position={[50, 100, 50]}
        intensity={0.8}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-near={0.5}
        shadow-camera-far={1000}
        shadow-camera-left={-300}
        shadow-camera-right={300}
        shadow-camera-top={300}
        shadow-camera-bottom={-300}
      />
      <directionalLight position={[-30, 50, -30]} intensity={0.3} />

      {/* Hemisphere light for subtle ambient color variation */}
      <hemisphereLight args={['#b1e1ff', '#b97a20', 0.2]} />

      {/* Grid (matching Python viewer's 200x50 grid) */}
      <Grid
        args={[200, 200]}
        position={[0, -0.01, 0]}
        cellSize={4}
        cellThickness={0.5}
        cellColor="#333344"
        sectionSize={20}
        sectionThickness={1}
        sectionColor="#444466"
        fadeDistance={300}
        fadeStrength={1}
        followCamera={false}
      />

      {/* Click background to deselect */}
      <mesh
        position={[0, -0.02, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={handleBackgroundClick}
        visible={false}
      >
        <planeGeometry args={[2000, 2000]} />
        <meshBasicMaterial />
      </mesh>

      {/* Orbit controls */}
      <OrbitControls
        ref={controlsRef}
        enableDamping
        dampingFactor={0.05}
        minDistance={5}
        maxDistance={500}
      />

      {/* Cabinet models */}
      {cabinets.map((cab) => (
        <CabinetModel
          key={cab.id}
          cabinet={cab}
          adjacency={detectAdjacency(cab, cabinets)}
          selected={cab.id === selectedId}
          onSelect={onSelect}
          showHardware={showHardware}
          showDrillHoles={showDrillHoles}
        />
      ))}
    </>
  );
}

// ── Main exported component ──────────────────────────────────────────────────
interface CabinetSceneProps {
  cabinets: CabinetData[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  showHardware: boolean;
  showDrillHoles: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export default function CabinetScene({
  cabinets,
  selectedId,
  onSelect,
  showHardware,
  showDrillHoles,
  className,
  style,
}: CabinetSceneProps) {
  return (
    <Canvas
      className={className}
      style={style}
      camera={{
        fov: 45,
        near: 0.1,
        far: 2000,
        position: [50, 40, 80],
      }}
      gl={{
        antialias: true,
        toneMapping: THREE.ACESFilmicToneMapping,
        toneMappingExposure: 1.0,
      }}
      onCreated={({ gl }) => {
        gl.shadowMap.enabled = true;
        gl.shadowMap.type = THREE.PCFShadowMap;
      }}
    >
      <color attach="background" args={['#1a1a2e']} />
      <SceneContent
        cabinets={cabinets}
        selectedId={selectedId}
        onSelect={onSelect}
        showHardware={showHardware}
        showDrillHoles={showDrillHoles}
      />
    </Canvas>
  );
}
